import Typography from "antd/lib/typography";
import React, { memo, useMemo } from "react";
import { Element as SlateElement, Text as SlateText } from "slate";

import { HeadingOne, HeadingTwo, HeadingThree, Citation, CodeBlock, Image } from "./editor/renderer";
import { deserializeMarkdownToRichTextDocument, validateSlateStructure } from "./editor/serializer";
import { RichTextRendererProps, CustomText, CustomElement } from "./editor/types";

type CodeElement = SlateElement & {
    type: "code";
    language?: string;
};

const RichTextRenderer: React.FC<RichTextRendererProps> = ({ document, markdown, children, className }) => {
    const { Text } = Typography;

    const tryDeserialize = (text: string): React.ReactNode | null => {
        try {
            const slateNodes = deserializeMarkdownToRichTextDocument(text);
            const validatedNodes = validateSlateStructure(slateNodes);
            return validatedNodes.map((node, index) => renderSlateNode(node, `root-${index}`));
        } catch {
            return null;
        }
    };

    const renderSlateNode = (node: SlateElement | SlateText, key?: string): React.ReactNode => {
        if (SlateText.isText(node)) {
            return renderStaticLeaf(node, key);
        }
        if (SlateElement.isElement(node)) {
            return renderStaticElement(node, key);
        }
        return null;
    };

    const renderStaticLeaf = (textNode: SlateText, key?: string): React.ReactNode => {
        const text = textNode.text.split("\n").map((line, index) => (
            <React.Fragment key={index}>
                {index > 0 && <br />}
                {line}
            </React.Fragment>
        ));
        let element: React.ReactNode = text;
        const formattedText = textNode as CustomText;

        if (formattedText.code) {
            element = <code key={key}>{element}</code>;
        } else {
            if (formattedText.bold) {
                element = <strong key={key}>{element}</strong>;
            }
            if (formattedText.italic) {
                element = <i key={key}>{element}</i>;
            }
            if (formattedText.underline) {
                element = <u key={key}>{element}</u>;
            }
            if (formattedText["strike-through"]) {
                element = <s key={key}>{element}</s>;
            }
            if (formattedText.link) {
                element = (
                    <a key={key} href={formattedText.link}>
                        {element}
                    </a>
                );
            }
        }
        return element;
    };

    const renderStaticElement = (element: SlateElement, key?: string): React.ReactNode => {
        if (!element.children || !Array.isArray(element.children)) {
            return null;
        }
        const children = element.children.map((child, index) => renderSlateNode(child, `child-${index}`));

        switch (element.type) {
            case "heading-one":
                return <HeadingOne key={key}>{children}</HeadingOne>;
            case "heading-two":
                return <HeadingTwo key={key}>{children}</HeadingTwo>;
            case "heading-three":
                return <HeadingThree key={key}>{children}</HeadingThree>;
            case "heading-four":
                return <h4 key={key}>{children}</h4>;
            case "heading-five":
                return <h5 key={key}>{children}</h5>;
            case "heading-six":
                return <h6 key={key}>{children}</h6>;
            case "block-quote":
            case "citation":
                return <Citation key={key}>{children}</Citation>;
            case "code":
                const codeContent = element.children.map(child => (SlateText.isText(child) ? child.text : "")).join("");
                return (
                    <CodeBlock key={key} language={(element as CodeElement)?.language || ""} isReadOnly={false}>
                        {codeContent}
                    </CodeBlock>
                );
            case "bulleted-list":
                return (
                    <ul key={key}>{element.children.map((child, index) => renderSlateNode(child, `list-${index}`))}</ul>
                );
            case "numbered-list":
                return (
                    <ol key={key}>{element.children.map((child, index) => renderSlateNode(child, `list-${index}`))}</ol>
                );
            case "list-item":
                return <li key={key}>{children}</li>;
            case "image":
                return (
                    <Image
                        key={key}
                        src={(element as CustomElement & { type: "image" })?.url || ""}
                        alt="Image"
                        isReadOnly={true}
                    >
                        {children} {/* mandatory for SlateJS framework */}
                    </Image>
                );
            case "paragraph":
            default:
                return (
                    <p key={key} className="last:mb-0!" data-element-kind={element.type}>
                        {element.children.length === 1 &&
                        SlateText.isText(element.children[0]) &&
                        !element.children[0].text ? (
                            <br />
                        ) : (
                            children
                        )}
                    </p>
                );
        }
    };

    const content = useMemo(() => {
        if (document) {
            return document.map((node, index) => renderSlateNode(node, `root-${index}`));
        }
        if (typeof children === "string" && children.trim()) {
            return tryDeserialize(children);
        }
        if (typeof markdown === "string" && markdown.trim()) {
            return tryDeserialize(markdown);
        }
        return null;
    }, [document, markdown, children]);

    return <Text className={className}>{content}</Text>;
};

RichTextRenderer.displayName = "RichTextRenderer";

export default memo(RichTextRenderer);
